import SimpleModal from "app/components/Modals/SimpleModal";
import {
  selectAllWeeklyCheckInTableData,
  selectSubordinatesWeeklyCheckinTableData,
  selectCheckInResponseSummaries,
  getCheckInResponseSummary,
  selectGetCheckInResponseSummaryStatus,
  selectWeeklyCheckInCompanySettings,
  selectSampleWeeklyCheckInActivityData,
} from "../slice";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectAllCompanyUsersById,
  selectCurrentUserAccountId,
  selectIsCurrentUserAdmin,
  selectSampleUsersInfoById,
} from "app/containers/Global/slice";
import { useEffect, useMemo, useState } from "react";
import Loading from "app/storybookComponents/Loading";
import { getOutOfFiveColor } from "app/containers/Dashboard/helpers";
import { trackModalOpened } from "utils/trackingFunctions";
import { WeeklyCheckInActivityEmailReportTableData } from "../types";
import { findCheckInRowByThread } from "../tableDataUpdaters";
import {
  getScoreSpan,
  getUserInfo,
  getViewWeeklyCheckInReportButton,
} from "../helpers";
import { Card } from "react-bootstrap";
import CheckInEmailReportModal from "./CheckInEmailReportModal";
import {
  WEEKLY_CHECK_IN_FEATURE_NAME,
  WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS,
} from "../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EmptyCard from "app/storybookComponents/Cards/EmptyCard";

interface CheckInResponseSummaryModalProps {
  onHide: () => void;
  show: boolean;
  threadId?: string;
  userAccountId?: number | null;
  isSample?: boolean;
  date?: string;
}

const CheckInResponseSummaryModal = ({
  onHide,
  show,
  threadId,
  userAccountId,
  isSample,
  date,
}: Readonly<CheckInResponseSummaryModalProps>) => {
  const dispatch = useAppDispatch();
  const allWeeklyCheckInTableData = useAppSelector(
    selectAllWeeklyCheckInTableData
  );
  const subordinatesWeeklyCheckInTableData = useAppSelector(
    selectSubordinatesWeeklyCheckinTableData
  );
  const sampleWeeklyCheckInActivityData = useAppSelector(
    selectSampleWeeklyCheckInActivityData
  );
  const getCheckInResponseSummaryStatus = useAppSelector(
    selectGetCheckInResponseSummaryStatus
  );
  const checkInResponseSummaries = useAppSelector(
    selectCheckInResponseSummaries
  );
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const sampleUsersById = useAppSelector(selectSampleUsersInfoById);
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const [emailReportModalState, setEmailReportModalState] = useState<{
    userAccountId: number | null;
    reports: WeeklyCheckInActivityEmailReportTableData["reports"];
  } | null>(null);

  // The modal can be opened from the admin (organization) table or the direct reports table, so we look in both
  const foundCheckInData = useMemo(
    () =>
      findCheckInRowByThread(allWeeklyCheckInTableData, threadId) ??
      findCheckInRowByThread(subordinatesWeeklyCheckInTableData, threadId),
    [allWeeklyCheckInTableData, subordinatesWeeklyCheckInTableData, threadId]
  );
  const completedDate = useMemo(
    () => new Date(date ?? foundCheckInData?.completedDate ?? ""),
    [date, foundCheckInData]
  );

  const checkInResponseSummariesMap = isSample
    ? sampleWeeklyCheckInActivityData?.analyses
    : checkInResponseSummaries;
  const users = isSample ? sampleUsersById : usersById;

  const foundSummary = threadId
    ? checkInResponseSummariesMap?.[threadId]
    : null;

  useEffect(() => {
    if (threadId && !isSample) {
      dispatch(getCheckInResponseSummary(threadId));
    }
  }, [dispatch, threadId, isSample]);

  useEffect(() => {
    if (show) {
      trackModalOpened("Check-In Response Summary");
    }
  }, [show]);

  const hideScore = weeklyCheckInCompanySettings?.visibilityMode !== "open";

  const getAllScores = () => {
    if (!foundCheckInData || hideScore) {
      return null;
    }
    return (
      <div className="row-gap-16px">
        {getScoreSpan("Mood score", foundCheckInData.moodScore)}
        {getScoreSpan("Progress score", foundCheckInData.progressScore)}
        {getScoreSpan("Readiness score", foundCheckInData.readinessScore)}
      </div>
    );
  };

  const getMoreInsights = (moreInsights?: string | null) => {
    if (!moreInsights) {
      return null;
    }

    return (
      <div className="column-gap-4px">
        <p className="grey-text">More Insights:</p>
        <p>{moreInsights}</p>
      </div>
    );
  };

  const getWinSpotlight = () => {
    if (
      !foundSummary?.winSpotlight ||
      !(
        foundSummary?.winSpotlight?.achievement &&
        foundSummary?.winSpotlight?.impact &&
        foundSummary?.winSpotlight?.recognition
      )
    ) {
      return null;
    }
    const magnitude = foundSummary?.winSpotlight?.magnitude ?? 0;
    const color = getOutOfFiveColor(magnitude / 2);

    return (
      <div className="column-gap-4px">
        <p className="grey-text">Win Spotlight:</p>
        <ul className="check-in-response-list">
          <li>Achievement: {foundSummary?.winSpotlight?.achievement}</li>
          <li>Impact: {foundSummary?.winSpotlight?.impact}</li>
          <li>Recognition: {foundSummary?.winSpotlight?.recognition}</li>
        </ul>
        {!hideScore && (
          <div className="d-flex">
            <div className={`out-of-5-span-tag ${color}`}>
              <p>Magnitude: {magnitude} out of 10</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getIssueSpotlight = () => {
    if (
      !foundSummary?.issueSpotlight ||
      !(
        foundSummary?.issueSpotlight?.issue &&
        foundSummary?.issueSpotlight?.impact &&
        foundSummary?.issueSpotlight?.action
      )
    ) {
      return null;
    }
    const severityScore = foundSummary?.issueSpotlight?.severity ?? 0;
    const color = getOutOfFiveColor(severityScore / 2, true);

    return (
      <div className="column-gap-4px">
        <p className="grey-text">Issue Spotlight:</p>
        <ul className="check-in-response-list">
          <li>Issue: {foundSummary?.issueSpotlight?.issue}</li>
          <li>Impact: {foundSummary?.issueSpotlight?.impact}</li>
          <li>Action: {foundSummary?.issueSpotlight?.action}</li>
        </ul>
        {!hideScore && (
          <div className="d-flex">
            <div className={`out-of-5-span-tag ${color}`}>
              <p>Severity: {severityScore} out of 10</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getConversationStarters = () => {
    if (!foundSummary?.conversationStarters?.length) {
      return null;
    }

    return (
      <Card
        className="column-gap-4px"
        style={{
          backgroundColor: "#F5F7FC",
          padding: "16px 24px",
        }}
      >
        <p className="grey-text">Conversation Starters:</p>
        <ul className="check-in-response-list">
          {foundSummary?.conversationStarters.map((starter) => (
            <li key={starter}>{starter}</li>
          ))}
        </ul>
      </Card>
    );
  };

  const getEmailReportButton = () => {
    const userInfo = userAccountId && usersById[userAccountId];
    const hasRequiredInfo = userInfo && foundSummary?.emailReports?.length;
    if (!hasRequiredInfo) {
      return null;
    }

    const onActionClick = () => {
      setEmailReportModalState({
        userAccountId: userAccountId ?? null,
        reports: foundSummary?.emailReports ?? [],
      });
    };

    return (
      <>
        <hr className="m-0" />
        <div style={{ width: "fit-content" }}>
          {getViewWeeklyCheckInReportButton(
            onActionClick,
            weeklyCheckInCompanySettings?.reportVisibilitySettings,
            !!isAdmin,
            userAccountId === currentUserAccountId,
            `View ${userInfo?.firstName ?? "User"}'s email report(s)`
          )}
        </div>
      </>
    );
  };

  const getResponseSummaryVisibilityBanner = () => {
    if (!weeklyCheckInCompanySettings?.visibilityDefault || !userAccountId) {
      return null;
    }

    let text = "";
    const usersFirstName = users[userAccountId]?.firstName ?? "this user";
    switch (weeklyCheckInCompanySettings?.visibilityDefault) {
      case WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.DEVELOPMENT:
        text = `This report is currently visible to ${usersFirstName}, their manager, their team leader(s), their reporting line managers, and admins.`;
        break;
      case WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.TEAM:
        text = `This report is currently visible to ${usersFirstName}, their manager, their team leader(s), and admins.`;
        break;
      case WEEKLY_CHECK_IN_VISIBILITY_DEFAULTS_OPTIONS.PRIVACY:
        text = `This report is currently visible to ${usersFirstName}, their manager, and admins.`;
        break;
    }

    return (
      <div className="warning-banner blue row-gap-12px align-items-center">
        <FontAwesomeIcon icon={["far", "eye"]} />
        <p className="fw-bold">
          {text}
          {isAdmin ? "" : " Reach out to an admin to learn more."}
        </p>
      </div>
    );
  };

  const getEmptyState = () => {
    return (
      <EmptyCard
        title="Check-in summary unavailable"
        bodyText="A summary couldn't be generated for this check-in. This can happen if the conversation was too brief, is still being processed, or if an error occurred during analysis."
      />
    );
  };

  const getModalHeader = () => (
    <>
      <hr className="m-0" />
      {getResponseSummaryVisibilityBanner()}
      <p>Check-In completed by</p>
      {getUserInfo(users, userAccountId)}
    </>
  );

  const getContent = () => {
    if (getCheckInResponseSummaryStatus === "loading") {
      return <Loading />;
    }
    if (!foundCheckInData?.wasConversationGraded) {
      return (
        <>
          {getModalHeader()}
          {getEmptyState()}
        </>
      );
    }

    return (
      <>
        {getModalHeader()}
        {getAllScores()}
        <div className="column-gap-16px">
          <div className="column-gap-4px">
            <p className="grey-text">Response Summary:</p>
            <p>{foundSummary?.primaryTheme}</p>
          </div>
          {getMoreInsights(foundSummary?.secondaryTheme)}
          {getWinSpotlight()}
          {getIssueSpotlight()}
          {getConversationStarters()}
        </div>
        <p className="grey-text">
          This {WEEKLY_CHECK_IN_FEATURE_NAME} was completed on{" "}
          {completedDate.toLocaleDateString()}
        </p>
        {getEmailReportButton()}
      </>
    );
  };

  const getModalTitle = () => {
    const user = userAccountId ? users[userAccountId] : null;
    if (!user) {
      return "Check-In Response Summary";
    }

    const { firstName } = user;

    const dateObj = new Date(completedDate);
    const date = dateObj.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
    });

    return `${firstName}'s ${date} Check-in`;
  };

  return (
    <>
      <CheckInEmailReportModal
        show={!!emailReportModalState}
        onHide={() => setEmailReportModalState(null)}
        userAccountId={emailReportModalState?.userAccountId}
        selectReports={emailReportModalState?.reports}
        date={foundCheckInData?.completedDate}
      />
      <SimpleModal
        show={show && !emailReportModalState}
        onHide={onHide}
        title={getModalTitle()}
      >
        {getContent()}
      </SimpleModal>
    </>
  );
};

export default CheckInResponseSummaryModal;
